import { dbManager } from './db_manager';
import { studyPlanService } from './studyPlanService';

export interface ExerciseItemRecord {
  id: string;
  user_id: string;
  exercise_type: 'pronunciation' | 'listening' | 'writing' | 'grammar_reading' | 'grammar_quiz' | 'flashcards_words' | 'flashcards_phrases';
  lang_code: string;
  level: string;
  item_id: string;
  status: 'seen' | 'mastered' | 'pending';
  times_practiced: number;
  last_score: number;
  next_review_date?: string | null;
  last_practiced_at: string;
  created_at: string;
}

class ExerciseProgressService {
  private localCache: Map<string, Set<string>> = new Map();

  private getCacheKey(userId: string, type: string, lang: string, level: string): string {
    return `ex_prog_${userId}_${type}_${lang}_${level}`;
  }

  /**
   * Registra a conclusão ou tentativa de um exercício específico.
   * Atualiza a continuidade, repetição espaçada e marca a tarefa no Plano de Estudos.
   */
  public async recordPractice(params: {
    userId: string | undefined;
    exerciseType: ExerciseItemRecord['exercise_type'];
    langCode: string;
    level: string;
    itemId: string;
    score?: number;
    isSuccess?: boolean;
  }): Promise<void> {
    const { userId, exerciseType, langCode, level, itemId, score = 100, isSuccess = true } = params;
    if (!userId || userId === 'guest' || !itemId) return;

    const cleanLang = (langCode || 'en').toLowerCase().split('-')[0];
    const lvlUpper = (level || 'A1').toUpperCase();
    const id = `${userId}_${exerciseType}_${cleanLang}_${lvlUpper}_${String(itemId).replace(/[^a-zA-Z0-9_-]/g, '_')}`;

    // Status: se errou ou nota baixa, fica pending para rever amanhã; se acertou, vira seen ou mastered
    const status = isSuccess && score >= 70 ? 'seen' : 'pending';

    // Salvar localmente em cache
    const cacheKey = this.getCacheKey(userId, exerciseType, cleanLang, lvlUpper);
    let cachedSet = this.localCache.get(cacheKey);
    if (!cachedSet) {
      cachedSet = new Set<string>();
      this.localCache.set(cacheKey, cachedSet);
    }
    cachedSet.add(String(itemId));

    // Salvar local no localStorage para offline
    try {
      const lsKey = `libritui_practiced_${userId}_${exerciseType}_${cleanLang}_${lvlUpper}`;
      const savedList = JSON.parse(localStorage.getItem(lsKey) || '[]');
      if (!savedList.includes(String(itemId))) {
        savedList.push(String(itemId));
        localStorage.setItem(lsKey, JSON.stringify(savedList));
      }
    } catch (e) {}

    // Salvar no D1 Cloudflare
    try {
      const now = new Date().toISOString();
      await dbManager.from('user_exercise_item_progress', userId).upsert({
        id,
        user_id: userId,
        exercise_type: exerciseType,
        lang_code: cleanLang,
        level: lvlUpper,
        item_id: String(itemId),
        status,
        times_practiced: 1,
        last_score: score,
        last_practiced_at: now,
        created_at: now
      });
    } catch (err) {
      console.warn('[ExerciseProgressService] Erro ao sincronizar item no D1:', err);
    }

    // Notificar e marcar no Plano de Estudos
    this.notifyStudyPlanTask(userId, cleanLang, exerciseType, String(itemId));
  }

  /**
   * Obtém a lista de IDs de itens já praticados para continuar de onde parou.
   */
  public async getPracticedItemIds(
    userId: string | undefined,
    exerciseType: ExerciseItemRecord['exercise_type'],
    langCode: string,
    level: string
  ): Promise<{ practicedIds: Set<string>; pendingReviewIds: Set<string> }> {
    const practicedIds = new Set<string>();
    const pendingReviewIds = new Set<string>();

    if (!userId || userId === 'guest') {
      return { practicedIds, pendingReviewIds };
    }

    const cleanLang = (langCode || 'en').toLowerCase().split('-')[0];
    const lvlUpper = (level || 'A1').toUpperCase();

    // 1. Tentar ler do localStorage
    try {
      const lsKey = `libritui_practiced_${userId}_${exerciseType}_${cleanLang}_${lvlUpper}`;
      const savedList = JSON.parse(localStorage.getItem(lsKey) || '[]');
      if (Array.isArray(savedList)) {
        savedList.forEach(id => practicedIds.add(String(id)));
      }
    } catch (e) {}

    // 2. Buscar no D1 (com timeout rápido para nunca travar a UI)
    try {
      const d1Promise = dbManager.from('user_exercise_item_progress', userId).select({
        exercise_type: exerciseType,
        lang_code: cleanLang,
        level: lvlUpper
      });
      const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject(new Error('D1 timeout')), 1500));
      const res: any = await Promise.race([d1Promise, timeoutPromise]);

      const rows: ExerciseItemRecord[] = Array.isArray(res) ? res : (res?.data || res?.results || []);
      rows.forEach(r => {
        practicedIds.add(String(r.item_id));
        if (r.status === 'pending') {
          pendingReviewIds.add(String(r.item_id));
        }
      });
    } catch (err) {
      console.warn('[ExerciseProgressService] Aviso ao carregar itens praticados do D1 (usando cache local):', err);
    }

    return { practicedIds, pendingReviewIds };
  }

  /**
   * Calcula o índice do próximo item a ser exibido para continuar de onde parou.
   */
  public getNextStartIndex<T extends { id?: string | number }>(
    items: T[],
    practicedIds: Set<string>,
    pendingIds: Set<string>
  ): number {
    if (!items || items.length === 0) return 0;

    // 1. Prioridade para exercícios pendentes de repetição
    if (pendingIds && pendingIds.size > 0) {
      const pendingIdx = items.findIndex((it, idx) => pendingIds.has(String(it.id || idx)));
      if (pendingIdx >= 0) return pendingIdx;
    }

    // 2. Primeiro item que ainda NÃO foi praticado
    const nextUnseenIdx = items.findIndex((it, idx) => !practicedIds.has(String(it.id || idx)));
    if (nextUnseenIdx >= 0) {
      return nextUnseenIdx;
    }

    // 3. Se todos já foram praticados, volta ao início (ciclo completo)
    return 0;
  }

  /**
   * Notifica e incrementa o progresso do plano de estudos diário.
   */
  public notifyStudyPlanTask(userId: string, langCode: string, exerciseType: string, itemId?: string) {
    const cleanLang = (langCode || 'en').toLowerCase().split('-')[0];

    // Mapeamento para o tipo de tarefa do plano
    let taskType = exerciseType;
    if (exerciseType === 'flashcards_words' || exerciseType === 'words') taskType = 'flashcards_words';
    else if (exerciseType === 'flashcards_phrases' || exerciseType === 'phrases') taskType = 'flashcards_phrases';

    // Disparar evento de item praticado para a Barra de Progresso Universal e listeners
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('study_plan_task_item_done', {
          detail: { type: taskType, lang: cleanLang, itemId }
        })
      );
    }
  }
}

export const exerciseProgressService = new ExerciseProgressService();
